import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { type Encargado, type EncargadoRequest } from '../types/encargado';
import { EncargadoService } from '../services/encargadoService';

interface EncargadoState {
  encargados: Encargado[];
  loading: boolean;
  error: string | null;
  selectedEncargado: Encargado | null;
  dialogOpen: boolean;
  fetchEncargados: () => Promise<void>;
  saveEncargado: (encargado: EncargadoRequest) => Promise<void>;
  deleteEncargado: (id: number) => Promise<void>;
  setSelectedEncargado: (encargado: Encargado | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;
}

export const useEncargadoStore = create<EncargadoState>()(
  devtools(
    (set, get) => ({
      encargados: [],
      loading: false,
      error: null,
      selectedEncargado: null,
      dialogOpen: false,

      fetchEncargados: async () => {
        set({ loading: true, error: null });
        try {
          const encargados = await EncargadoService.getEncargados();
          set({ encargados, loading: false });
        } catch (error) {
          set({ error: 'Error al cargar encargados', loading: false });
          console.error('Error fetching encargados:', error);
        }
      },

      saveEncargado: async (encargado: EncargadoRequest) => {
        set({ loading: true, error: null });
        try {
          await EncargadoService.saveEncargado(encargado);
          await get().fetchEncargados();
          set({ loading: false, dialogOpen: false, selectedEncargado: null });
        } catch (error) {
          set({ error: 'Error al guardar encargado', loading: false });
          console.error('Error saving encargado:', error);
          throw error;
        }
      },

      deleteEncargado: async (id: number) => {
        set({ loading: true, error: null });
        try {
          await EncargadoService.deleteEncargado(id);
          set((state) => ({
            encargados: state.encargados.filter((e) => e.id !== id),
            loading: false,
          }));
        } catch (error) {
          set({ error: 'Error al eliminar encargado', loading: false });
          console.error('Error deleting encargado:', error);
          throw error;
        }
      },

      setSelectedEncargado: (encargado: Encargado | null) => set({ selectedEncargado: encargado }),
      openDialog: () => set({ dialogOpen: true }),
      closeDialog: () => set({ dialogOpen: false, selectedEncargado: null }),
      clearError: () => set({ error: null }),
    }),
    { name: 'encargado-store' }
  )
);
